import { useState } from 'react';
import { useAuth } from '@/lib/api/auth';
import { api } from '@/lib/api';
import type { ChildInfo, ChildLevelInfo } from '@/lib/api/types';

interface HabitLevelTabProps {
  children: ChildInfo[];
  levels: ChildLevelInfo[];
}

function getLevelInfo(experience: number, levels: ChildLevelInfo[]) {
  if (!levels || levels.length === 0) {
    return {
      level: 1,
      name: '未知等级',
      icon: 'ri-star-fill',
      color: '#93c5fd',
      min_experience: 0,
      max_experience: 999,
      progress: 0,
      nextName: '下一级',
    };
  }

  let currentLevel = levels[0];
  for (const level of levels) {
    if (level.min_experience <= experience) {
      currentLevel = level;
    } else {
      break;
    }
  }

  const nextLevel = levels.find(l => l.level === currentLevel.level + 1);
  let progress = 100;
  if (nextLevel && nextLevel.min_experience !== undefined && currentLevel.min_experience !== undefined) {
    const range = nextLevel.min_experience - currentLevel.min_experience;
    if (range > 0) {
      progress = Math.min(100, Math.round(((experience - currentLevel.min_experience) / range) * 100));
    }
  }

  return {
    level: currentLevel.level,
    name: currentLevel.name,
    icon: currentLevel.icon,
    color: currentLevel.color,
    min_experience: currentLevel.min_experience,
    max_experience: currentLevel.max_experience,
    progress,
    nextName: nextLevel?.name || '满级',
  };
}

const getDefaultLevels = (): ChildLevelInfo[] => [
  { id: 1, level: 1, name: '星星宝宝', icon: 'star', color: '#FFC107', min_experience: 0, max_experience: 999 },
  { id: 2, level: 2, name: '小太阳', icon: 'sun', color: '#FF9800', min_experience: 1000, max_experience: 4999 },
  { id: 3, level: 3, name: '智慧星', icon: 'brain', color: '#4CAF50', min_experience: 5000, max_experience: 14999 },
  { id: 4, level: 4, name: '小达人', icon: 'trophy', color: '#2196F3', min_experience: 15000, max_experience: 34999 },
  { id: 5, level: 5, name: '超级冠军', icon: 'crown', color: '#9C27B0', min_experience: 35000 },
];

const getIcon = (icon: string) => {
  // 如果已经是 RemixIcon 格式（如 ri-star-fill），直接返回
  if (icon && icon.startsWith('ri-')) return icon;
  // 否则从旧格式映射
  const iconMap: Record<string, string> = {
    'star': 'ri-star-fill',
    'sun': 'ri-sun-fill',
    'brain': 'ri-brain-fill',
    'trophy': 'ri-trophy-fill',
    'crown': 'ri-crown-fill',
  };
  return iconMap[icon] || 'ri-star-fill';
};

export default function HabitLevelTab({ children, levels }: HabitLevelTabProps) {
  const { currentFamily } = useAuth();
  const [ratio, setRatio] = useState(1.0);
  const [showLevelTable, setShowLevelTable] = useState(false);
  const [loading, setLoading] = useState(false);

  // 优先使用后端返回的动态等级数据，无数据时使用默认等级
  const displayLevels = levels && levels.length > 0 ? levels : getDefaultLevels();

  const handleUpdateRatio = async (newRatio: number) => {
    if (!currentFamily) return;
    setLoading(true);
    try {
      await api.family.updateFamily(currentFamily.id, {
        level_ratio: newRatio
      });
      setRatio(newRatio);
    } catch (error) {
      console.error('Failed to update ratio:', error);
    } finally {
      setLoading(false);
    }
  };

  // 为每个孩子计算等级信息
  const childrenWithLevels = children.map(child => {
    const adjustedExperience = Math.round(child.experience / ratio);
    const lv = getLevelInfo(adjustedExperience, displayLevels);
    const getAvatarUrl = () => {
      if (child.avatar_url) return child.avatar_url;
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(child.nickname || child.name)}&background=${child.avatar_color.replace('#', '')}&color=fff`;
    };
    return { child, lv, adjustedExperience, getAvatarUrl };
  });

  return (
    <div className="space-y-5">
      {/* Ratio Setting */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#1a56db]/10 flex-shrink-0">
            <i className="ri-settings-3-fill text-[#1a56db] text-xl"></i>
          </div>
          <div>
            <h3 className="font-bold text-[#0d1f5c] text-base">积分比值设置</h3>
            <p className="text-gray-400 text-xs mt-0.5">根据家庭积分难度，调整等级计算比值</p>
          </div>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 mb-4">
          <p className="text-xs text-[#1a56db] leading-relaxed">
            <i className="ri-information-line mr-1"></i>
            不同家庭积分奖励幅度不同，通过设置比值可以让各家庭的等级评估更公平。
            例如设定比值为2，则实际积分除以2后对照等级表，避免高奖励家庭等级虚高。
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">当前比值</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleUpdateRatio(Math.max(0.5, Math.round((ratio - 0.5) * 10) / 10))}
                  disabled={loading}
                  className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-[#1a56db] hover:text-[#1a56db] cursor-pointer transition-colors disabled:opacity-50"
                >
                  <i className="ri-subtract-line text-sm"></i>
                </button>
                <span className="w-16 text-center font-black text-[#1a56db] text-lg">×{ratio.toFixed(1)}</span>
                <button
                  onClick={() => handleUpdateRatio(Math.min(10, Math.round((ratio + 0.5) * 10) / 10))}
                  disabled={loading}
                  className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-[#1a56db] hover:text-[#1a56db] cursor-pointer transition-colors disabled:opacity-50"
                >
                  <i className="ri-add-line text-sm"></i>
                </button>
              </div>
            </div>
            <input
              type="range"
              min={5}
              max={100}
              step={5}
              value={ratio * 10}
              onChange={e => setRatio(parseInt(e.target.value) / 10)}
              onMouseUp={e => handleUpdateRatio(parseInt((e.target as HTMLInputElement).value) / 10)}
              className="w-full accent-[#1a56db]"
            />
            <div className="flex justify-between text-[10px] text-gray-400 mt-1">
              <span>×0.5（严格）</span>
              <span>×10（宽松）</span>
            </div>
          </div>
        </div>
        <div className="mt-4 p-3 bg-gray-50 rounded-xl text-xs text-gray-500 flex items-center gap-2">
          <i className="ri-lightbulb-line text-amber-500"></i>
          当前比值 ×{ratio.toFixed(1)}：孩子获得 <span className="font-bold text-[#0d1f5c]">100积分</span> 后，等级经验 = <span className="font-bold text-[#1a56db]">{Math.round(100 / ratio)}经验</span>
        </div>
      </div>

      {/* Children Level Cards */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#0d1f5c] flex items-center gap-2">
            <i className="ri-user-smile-fill text-[#1a56db]"></i>孩子当前等级
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {childrenWithLevels.map(({ child, lv, adjustedExperience, getAvatarUrl }) => (
            <div key={child.id} className="bg-white rounded-2xl p-5 border border-gray-100 hover:-translate-y-0.5 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-shrink-0">
                  <img src={getAvatarUrl()} alt={child.nickname || child.name} className="w-12 h-12 rounded-2xl object-cover object-top" onError={(e) => {
                    (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(child.nickname || child.name)}&background=3b82f6&color=fff`;
                  }} />
                  <div className={`absolute -bottom-1.5 -right-1.5 w-6 h-6 flex items-center justify-center rounded-full border-2 border-white text-[9px] font-black text-white`} style={{ background: lv.color }}>
                    {lv.level}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-bold text-[#0d1f5c] text-sm">{child.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: lv.color + '20', color: lv.color }}>{lv.name}</span>
                  </div>
                  <p className="text-gray-400 text-xs">习惯经验：{adjustedExperience.toLocaleString()} / {lv.max_experience != null ? lv.max_experience.toLocaleString() : '∞'}</p>
                </div>
              </div>

              {/* Level Progress */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-gray-500">Lv.{lv.level} {lv.name}</span>
                  <span className="text-xs text-gray-500">→ Lv.{Math.min(lv.level + 1, displayLevels.length)} {lv.nextName}</span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${lv.progress}%`, background: `linear-gradient(90deg, ${lv.color}aa, ${lv.color})` }}
                  ></div>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-gray-400">{lv.progress}% 进度</span>
                  <span className="text-[10px]" style={{ color: lv.color }}>
                    <i className={`${getIcon(lv.icon)} mr-0.5`}></i>{lv.name}
                  </span>
                </div>
              </div>

              {/* Habit Points Info */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl text-xs">
                <div className="flex items-center gap-1 text-gray-500">
                  <i className="ri-star-fill text-amber-400"></i>
                  总积分 <span className="font-bold text-[#0d1f5c]">{child.total_points}</span>
                </div>
                <div className="w-px h-3 bg-gray-200"></div>
                <div className="flex items-center gap-1 text-gray-500">
                  <i className="ri-flashlight-fill text-[#1a56db]"></i>
                  等级经验 <span className="font-bold text-[#1a56db]">{adjustedExperience}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Level Table */}
      <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
        <button
          onClick={() => setShowLevelTable(!showLevelTable)}
          className="w-full flex items-center justify-between p-5 cursor-pointer hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-amber-50">
              <i className="ri-bar-chart-fill text-amber-500 text-lg"></i>
            </div>
            <span className="font-bold text-[#0d1f5c]">全部等级表 (共{displayLevels.length}级)</span>
          </div>
          {showLevelTable ? <i className="ri-arrow-up-s-line text-gray-400 text-xl"></i> : <i className="ri-arrow-down-s-line text-gray-400 text-xl"></i>}
        </button>
        {showLevelTable && (
          <div className="px-5 pb-5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left">
                    <th className="pb-3 text-xs font-semibold text-gray-400 w-12">等级</th>
                    <th className="pb-3 text-xs font-semibold text-gray-400">称号</th>
                    <th className="pb-3 text-xs font-semibold text-gray-400 text-right">经验需求</th>
                    <th className="pb-3 text-xs font-semibold text-gray-400 text-right">区间积分(×{ratio.toFixed(1)})</th>
                  </tr>
                </thead>
                <tbody className="space-y-1">
                  {displayLevels.map((lv, idx) => (
                    <tr key={lv.level} className={idx % 2 === 0 ? 'bg-gray-50/50' : ''}>
                      <td className="py-2.5 px-2 rounded-l-lg">
                        <div className="w-7 h-7 flex items-center justify-center rounded-full text-white text-xs font-black" style={{ background: lv.color }}>
                          {lv.level}
                        </div>
                      </td>
                      <td className="py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 flex items-center justify-center rounded-lg" style={{ background: lv.color + '20' }}>
                            <i className={`${getIcon(lv.icon)} text-xs`} style={{ color: lv.color }}></i>
                          </div>
                          <span className="font-medium text-gray-700">{lv.name}</span>
                        </div>
                      </td>
                      <td className="py-2.5 text-right">
                        <span className="text-xs text-gray-600">
                          {(lv.min_experience ?? 0).toLocaleString()} - {lv.max_experience == null ? '∞' : lv.max_experience.toLocaleString()}
                        </span>
                      </td>
                      <td className="py-2.5 text-right rounded-r-lg">
                        <span className="text-xs font-medium" style={{ color: lv.color }}>
                          {Math.round((lv.min_experience ?? 0) * ratio).toLocaleString()} - {lv.max_experience == null ? '∞' : Math.round(lv.max_experience * ratio).toLocaleString()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-3 flex items-start gap-1.5">
              <i className="ri-information-line text-[#1a56db] flex-shrink-0 mt-0.5"></i>
              等级经验仅统计通过习惯规则获得/扣除的积分，积分兑换消耗不计入等级计算。
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
